import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArrowTopRightOnSquareIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL;

function authorizationHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
}

async function responseData(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.detail || 'The request could not be completed.');
  }
  return data;
}

export default function FirmRetainerPage() {
  const { caseId } = useParams();
  const fileInputRef = useRef(null);
  const [agreements, setAgreements] = useState([]);
  const [debtors, setDebtors] = useState([]);
  const [selectedDebtorId, setSelectedDebtorId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadRetainers = useCallback(async () => {
    const [agreementResponse, statusResponse] = await Promise.all([
      fetch(`${API_URL}/case/${caseId}/retainer-agreement/`, {
        credentials: 'include',
        headers: authorizationHeaders(),
      }),
      fetch(`${API_URL}/case/${caseId}/retainer-status/`, {
        credentials: 'include',
        headers: authorizationHeaders(),
      }),
    ]);
    const [agreementData, statusData] = await Promise.all([
      responseData(agreementResponse),
      responseData(statusResponse),
    ]);
    const nextAgreements = Array.isArray(agreementData) ? agreementData : [];
    const nextDebtors = Array.isArray(statusData) ? statusData : [];
    setAgreements(nextAgreements);
    setDebtors(nextDebtors);
    setSelectedDebtorId((current) => {
      if (current && nextDebtors.some((debtor) => String(debtor.debtor_id) === String(current))) {
        return current;
      }
      const unsignedDebtor = nextDebtors.find((debtor) => !debtor.signed);
      return String((unsignedDebtor || nextDebtors[0])?.debtor_id || '');
    });
  }, [caseId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadRetainers()
      .catch((err) => {
        if (active) setError(err.message || 'Failed to load the retainer agreements.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [loadRetainers]);

  const selectedDebtor = debtors.find(
    (debtor) => String(debtor.debtor_id) === String(selectedDebtorId),
  );

  const handleUpload = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (!selectedDebtorId) {
      setError('Select the debtor who signed the agreement.');
      return;
    }
    if (selectedDebtor?.signed) {
      setError('This debtor already has a signed retainer on file. Delete it first if it needs to be replaced.');
      return;
    }
    if (!selectedFile) {
      setError('Choose the scanned retainer PDF.');
      return;
    }

    const formData = new FormData();
    formData.append('debtor_id', selectedDebtorId);
    formData.append('upload', selectedFile);
    setUploading(true);
    try {
      const response = await fetch(`${API_URL}/case/${caseId}/retainer-agreement/`, {
        method: 'POST',
        credentials: 'include',
        headers: authorizationHeaders(),
        body: formData,
      });
      await responseData(response);
      setSuccess(`Signed retainer uploaded for ${selectedDebtor?.debtor_full_name || 'the debtor'}.`);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadRetainers();
    } catch (err) {
      setError(err.message || 'The signed retainer could not be uploaded.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (agreement) => {
    if (!window.confirm(`Delete the signed retainer for ${agreement.debtor_full_name || 'this debtor'}?`)) {
      return;
    }
    setError('');
    setSuccess('');
    setDeletingId(agreement.id);
    try {
      const response = await fetch(`${API_URL}/case/${caseId}/retainer-agreement/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          ...authorizationHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: agreement.id }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'The retainer agreement could not be deleted.');
      }
      setSuccess('The signed retainer was deleted.');
      await loadRetainers();
    } catch (err) {
      setError(err.message || 'The retainer agreement could not be deleted.');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-600">Loading retainer agreements…</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Retainer Agreements</h1>
        <p className="mt-1 text-sm text-gray-600">
          Review electronically signed agreements or upload a physically signed and scanned PDF.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          <CheckCircleIcon className="h-5 w-5" />
          {success}
        </div>
      )}

      <form onSubmit={handleUpload} className="rounded-lg border border-indigo-100 bg-white p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-indigo-50 p-2 text-indigo-600">
            <ArrowUpTrayIcon className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Upload a physically signed retainer</h2>
            <p className="text-sm text-gray-600">
              Select the filing debtor whose signature appears on the scanned agreement.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Debtor</span>
            <select
              value={selectedDebtorId}
              onChange={(event) => setSelectedDebtorId(event.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              {debtors.length === 0 && <option value="">No filing debtors found</option>}
              {debtors.map((debtor, index) => (
                <option key={debtor.debtor_id} value={debtor.debtor_id}>
                  {index === 0 ? 'Debtor 1 — ' : 'Debtor 2 — '}
                  {debtor.debtor_full_name}
                  {debtor.signed ? ' (signed agreement on file)' : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Scanned agreement PDF</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
              className="block w-full rounded-md border border-gray-300 bg-white text-sm text-gray-700 file:mr-4 file:border-0 file:bg-indigo-50 file:px-4 file:py-2 file:font-medium file:text-indigo-700 hover:file:bg-indigo-100"
            />
          </label>
        </div>

        {selectedDebtor?.signed && (
          <p className="mt-3 text-sm text-amber-700">
            This debtor already has a signed agreement. Delete the existing agreement before uploading a replacement.
          </p>
        )}

        <button
          type="submit"
          disabled={uploading || !selectedDebtorId || !selectedFile || selectedDebtor?.signed}
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ArrowUpTrayIcon className="h-5 w-5" />
          {uploading ? 'Uploading…' : 'Upload signed retainer'}
        </button>
      </form>

      <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Signed retainers on file</h2>
        {agreements.length === 0 ? (
          <div className="mt-4 rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
            No signed retainer agreements have been received yet.
          </div>
        ) : (
          <div className="mt-4 divide-y divide-gray-100">
            {agreements.map((agreement) => (
              <div key={agreement.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-start gap-3">
                  <DocumentTextIcon className="mt-0.5 h-6 w-6 flex-none text-indigo-500" />
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">
                      {agreement.debtor_full_name || 'Unknown signer'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {agreement.debtor_email || 'No email'} · {new Date(agreement.created_at).toLocaleString()}
                    </div>
                    <div className="mt-1 text-xs font-medium uppercase tracking-wide text-gray-400">
                      {agreement.upload_source === 'firm' ? 'Uploaded by firm' : 'Electronically signed by client'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:flex-none">
                  <a
                    href={agreement.upload_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800"
                  >
                    View PDF
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  </a>
                  <button
                    type="button"
                    onClick={() => handleDelete(agreement)}
                    disabled={deletingId === agreement.id}
                    className="inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                    {deletingId === agreement.id ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
